import type { CTAActionType } from "./ThinkingStep/cta-types";

/** Phase floors for Story Bible CTA progress (monotonic; never decrease during a run). */
export const STORY_BIBLE_CTA_PROGRESS = {
  started: 0.06,
  toolRunning: 0.14,
  toolComplete: 0.72,
  contentApplied: 0.86,
  entityReady: 0.96,
  done: 1,
} as const;

const CAST_APPLY_SPAN = STORY_BIBLE_CTA_PROGRESS.contentApplied - STORY_BIBLE_CTA_PROGRESS.toolComplete;

export function getExpectedToolForStoryBibleCta(ctaType: CTAActionType): string {
  switch (ctaType) {
    case "generate_trait":
    case "rewrite_trait":
      return "regenerate_trait";
    case "generate_worldbuilding_trait":
    case "rewrite_worldbuilding_trait":
      return "regenerate_worldbuilding_trait";
    case "generate_synopsis":
      return "synopsis_generate";
    case "rewrite_synopsis":
      return "synopsis_rewrite";
    default:
      return ctaType;
  }
}

export function toolNameMatchesStoryBibleCta(ctaType: CTAActionType, toolName: string): boolean {
  const expected = getExpectedToolForStoryBibleCta(ctaType);
  const normalized = toolName.toLowerCase();
  return normalized === expected || normalized === expected.replace(/_/g, " ");
}

/** Rough expected tool runtime for in-flight progress creep (ms). */
export function estimateStoryBibleCtaToolDurationMs(ctaType: CTAActionType): number {
  switch (ctaType) {
    case "generate_chapter":
    case "rewrite_chapter":
      return 50_000;
    case "generate_outline":
      return 75_000;
    case "rewrite_outline":
      return 75_000;
    case "generate_cast":
      return 90_000;
    case "generate_synopsis":
    case "rewrite_synopsis":
      return 35_000;
    default:
      return 28_000;
  }
}

export function parseCastGeneratedCountFromToolSummary(summary: string | undefined): number | null {
  if (!summary) return null;
  const match = summary.match(/Generated\s+(\d+)\s+characters?/i);
  if (!match) return null;
  const n = Number.parseInt(match[1], 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function computeCastApplyProgress(
  appliedCount: number,
  expectedCount: number | null,
): number {
  if (appliedCount <= 0) {
    return STORY_BIBLE_CTA_PROGRESS.toolComplete;
  }
  const denominator = expectedCount != null && expectedCount > 0
    ? expectedCount
    : appliedCount;
  const ratio = Math.min(1, appliedCount / denominator);
  return STORY_BIBLE_CTA_PROGRESS.toolComplete + CAST_APPLY_SPAN * ratio;
}

export function mergeStoryBibleCtaProgress(current: number, next: number): number {
  return Math.max(current, Math.min(1, next));
}

/** Primary button fill gradient used by Story Bible CTAs (matches legacy generate-cast button styling). */
export function primaryCtaFillGradient(progress: number): string {
  const percent = Math.round(Math.max(0, Math.min(1, progress)) * 100);
  return `linear-gradient(90deg, var(--primary-cta) ${percent}%, var(--grey-2) ${percent}%)`;
}

export function computeToolRunningDisplayProgress(
  phaseProgress: number,
  toolStartedAt: number,
  ctaType: CTAActionType,
): number {
  const elapsed = Date.now() - toolStartedAt;
  const duration = estimateStoryBibleCtaToolDurationMs(ctaType);
  const ratio = Math.min(1, elapsed / duration);
  const creep = STORY_BIBLE_CTA_PROGRESS.toolRunning
    + (STORY_BIBLE_CTA_PROGRESS.toolComplete - STORY_BIBLE_CTA_PROGRESS.toolRunning) * ratio;
  const capped = Math.min(STORY_BIBLE_CTA_PROGRESS.toolComplete - 0.02, creep);
  return mergeStoryBibleCtaProgress(phaseProgress, capped);
}
